import { useCallback, useEffect, useState } from 'react';

import { useFirebaseAuth } from '../auth/FirebaseAuthContext';
import {
  clearUserProfile,
  getUserProfile,
  readCachedUserProfile,
  subscribeUserProfile,
  type UserProfile,
} from '../services/userProfile';

/** Cached profile for a verified, non-guest account. Others get `profile: null`. */
export const useUserProfile = () => {
  const { user } = useFirebaseAuth();
  const uid = user?.uid ?? null;
  const eligible = !!user && !user.isAnonymous && user.emailVerified;

  const [profile, setProfile] = useState<UserProfile | null>(() =>
    readCachedUserProfile(uid)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user) {
      clearUserProfile();
      setProfile(null);
      setLoading(false);
      setError(null);
      return;
    }
    if (!eligible) {
      setProfile(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const unsubscribe = subscribeUserProfile((next, nextUid) => {
      if (!cancelled && nextUid === user.uid) setProfile(next);
    });

    const cached = readCachedUserProfile(user.uid);
    if (cached) {
      setProfile(cached);
      setLoading(false);
      setError(null);
      return () => {
        cancelled = true;
        unsubscribe();
      };
    }

    // Don't keep the previous account's name on screen while this one loads.
    setProfile(null);
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const token = await user.getIdToken();
        const data = await getUserProfile(token, user.uid);
        if (!cancelled) setProfile(data);
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setProfile(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [eligible, uid, user]);

  const refresh = useCallback(async (): Promise<UserProfile | null> => {
    if (!user || !eligible) return null;
    const token = await user.getIdToken();
    return getUserProfile(token, user.uid, { force: true });
  }, [eligible, user]);

  return { profile, loading, error, eligible, refresh };
};
